import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Cigarette, Heart, Brain, Users } from 'lucide-react';
import CTA from '../../components/CTA';

const CarrickfergusQuitSmoking: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Stop Smoking Carrickfergus | Vaping Cessation Castle Town</title>
        <meta
          name="description"
          content="Quit smoking hypnotherapy serving Carrickfergus from Belfast clinic (12 miles). Hospitality worker smoking cessation support. Honest success rates 20-30%."
        />
        <meta name="keywords" content="quit smoking Carrickfergus, stop smoking Castle Town, hypnotherapy smoking cessation Greenisland, quit vaping Carrickfergus, hospitality worker smoking" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/carrickfergus-quit-smoking/" />
        <meta property="og:title" content="Stop Smoking Carrickfergus | Vaping Cessation Castle Town" />
        <meta property="og:description" content="Intensive 2-hour quit smoking hypnotherapy serving Carrickfergus. Hospitality worker support, transparent pricing, lifetime support." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Smoking Cessation Carrickfergus",
            "description": "CNHC registered hypnotherapy for smoking cessation, serving Carrickfergus and East Antrim from Belfast clinic. Intensive 2-hour session with lifetime support and money-back guarantee. Hospitality worker smoking cessation specialist.",
            "url": "https://www.sharonclare-hypnotherapy.com/carrickfergus-quit-smoking/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Smoking Cessation Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Carrickfergus",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Antrim"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does quit smoking hypnotherapy cost in Carrickfergus?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The intensive 2-hour quit smoking session includes lifetime support and a money-back guarantee. For clients who prefer a gradual approach, a 3-session package is also available. To put this in perspective: a 20-a-day smoker spends thousands of pounds a year on cigarettes. You recoup the cost of the session in just 3 weeks of not smoking. For hospitality workers in Carrickfergus, this represents less than one week's wages for potentially life-changing results."
                }
              },
              {
                "@type": "Question",
                "name": "What are realistic success rates for quit smoking hypnotherapy?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I'm honest about success rates because trust matters. Research shows that at 12 months, approximately 20-30% of motivated clients remain smoke-free with hypnotherapy support. While some studies report higher short-term rates, long-term success is more modest. The evidence for hypnotherapy is mixed - some studies show good results, others show less dramatic effects. What matters most is your genuine motivation to quit. Hypnotherapy works best when you're truly ready to become a non-smoker, not when you're being pressured by others."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help hospitality workers quit smoking?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Hospitality workers face unique smoking challenges - smoking breaks as stress relief from difficult customers, the social ritual of smoking with coworkers, concern about cigarette breath when serving customers, and the habit of smoking after shifts. Hypnotherapy addresses these specific triggers by rewiring the subconscious patterns that link work stress with cigarettes, developing alternative behaviors for break times, and building confidence in managing customer service pressure without needing to smoke. The approach is tailored to hospitality industry smoking patterns."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for vaping cessation?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. The approach works equally well for vaping cessation as it does for cigarettes. While vaping is marketed as 'healthier' than smoking, it still maintains the nicotine addiction and the psychological habit. Many people who switched from smoking to vaping find themselves just as dependent, vaping constantly throughout the day. The hypnotherapy program addresses both the physical nicotine addiction and the psychological patterns that keep you reaching for your vape."
                }
              },
              {
                "@type": "Question",
                "name": "How far is Belfast from Carrickfergus for quit smoking sessions?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Belfast is just 12 miles south of Carrickfergus via the A2 coastal route and M2 motorway. By car, the journey takes 15-20 minutes in typical traffic conditions. Many Carrickfergus residents already travel to Belfast regularly for work or shopping, making this a familiar route. The short journey makes professional smoking cessation support highly accessible. Online sessions are also available if you prefer the convenience of attending from home."
                }
              },
              {
                "@type": "Question",
                "name": "Why focus on fresh breath for hospitality workers?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "For hospitality workers serving customers face-to-face, cigarette breath can be genuinely embarrassing and unprofessional. Tour guides at Carrickfergus Castle, hotel staff, restaurant servers, and retail workers all interact closely with customers. Smoking before or during shifts means serving customers while smelling of cigarettes. Quitting smoking means showing up to work fresh, confident, and professional - no longer worrying about whether customers can smell cigarette smoke on your breath or clothes. This professional confidence is a significant quality-of-life improvement."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Stop Smoking Hypnotherapy for Carrickfergus
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Intensive 2-hour session with lifetime support and money-back guarantee
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">12 miles</span> from Carrickfergus via A2/M2 • <span className="font-semibold">15-20 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Online sessions available • Flexible scheduling for hospitality workers</p>
            </div>
            <div className="bg-white rounded-xl p-6 mb-8 max-w-2xl mx-auto">
              <div className="flex items-start space-x-4">
                <Heart className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div className="text-left">
                  <p className="text-lg font-semibold text-gray-900 mb-2">Investment That Pays for Itself</p>
                  <p className="text-gray-700">
                    <span className="font-semibold">One intensive session</span> vs. <span className="font-semibold">thousands of pounds a year on cigarettes</span>
                  </p>
                  <p className="text-accent-700 font-medium mt-1">Recoup your investment in just 3 weeks</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Quit Smoking for Good in Carrickfergus
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Carrickfergus or East Antrim and ready to quit smoking or vaping, you don't need to search for local support that might not exist. I serve the Carrickfergus community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, located just 12 miles south via the A2 coastal route and M2 motorway. The journey takes only 15-20 minutes by car, making professional smoking cessation support genuinely accessible to residents of this historic castle town.
              </p>
              <p>
                Carrickfergus has a population of approximately 28,000 people, with an economy centered on tourism, hospitality, and heritage services around the magnificent Norman castle. If you work in hospitality at local hotels, serve customers at castle tours, work retail in town centre, or handle the pressures of customer-facing roles, you likely face unique smoking challenges. Smoking breaks become stress relief from difficult customers, the social ritual of smoking with coworkers feels essential, and concern about cigarette breath when serving customers creates guilt and embarrassment. You've probably tried to quit before - maybe multiple times. But here you are, still smoking, still spending money you can't afford, still feeling controlled by cigarettes.
              </p>
              <p>
                Here's the truth: quitting smoking isn't about willpower. If it were, you'd have quit already. The physical addiction to nicotine is actually relatively short-lived - it's out of your system within 72 hours. What keeps you smoking is the psychological habit: the automatic reach for a cigarette during your break, the stress relief ritual after a difficult shift, the social bonding over cigarettes with coworkers, the identity of being "a smoker." This is where hypnotherapy excels.
              </p>
              <p>
                My approach uses solution-focused hypnotherapy to rewire the subconscious habits that keep you smoking. We work on changing your mindset from "giving up" something you enjoy to freeing yourself from something that controls you. By addressing the basal ganglia autopilot patterns, the cue-routine-reward loops, and the emotional associations with smoking, hypnotherapy helps you become a non-smoker naturally - without cravings, without irritability, without feeling deprived.
              </p>
              <p>
                I'm honest about results: research shows that at 12 months, approximately 20-30% of motivated clients remain smoke-free with hypnotherapy support. The evidence for hypnotherapy is mixed - some studies show good results, others show less dramatic effects. Long-term success depends heavily on your genuine motivation to quit. Hypnotherapy isn't magic. It works best when you're truly ready to become a non-smoker. But if you're committed, it's one of the most effective approaches available. The intensive 2-hour session includes lifetime support and a money-back guarantee. To put that in perspective: a 20-a-day smoker spends thousands of pounds a year on cigarettes. You'll recoup the cost of the session in just 3 weeks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              How Quit Smoking Hypnotherapy Works
            </h2>
            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Brain className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Understanding the Habit Neuroscience
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Smoking is controlled by your basal ganglia - the autopilot part of your brain that runs habits without conscious thought. This is why you can light a cigarette without even remembering deciding to do it. Every habit follows a cue-routine-reward loop: stress (cue) → cigarette (routine) → temporary relief (reward). Your brain has learned this pattern so deeply it happens automatically.
                    </p>
                    <p className="text-gray-700">
                      Trying to quit with willpower means fighting your basal ganglia with your prefrontal cortex (conscious control). This works briefly, but willpower depletes throughout the day. By evening, when you're tired and stressed after a long shift, the autopilot wins and you smoke. Hypnotherapy works differently: it rewires the subconscious habit patterns in your basal ganglia, so you don't need to rely on willpower at all. You simply become a non-smoker naturally.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      The Intensive 2-Hour Session
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Most hypnotherapy for smoking cessation is delivered in a single intensive 2-hour session. This isn't a magic wand that removes all cravings instantly, but it's remarkably effective for changing your mindset and breaking the psychological addiction. The session includes:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Understanding your smoking triggers:</strong> We identify your specific cue-routine-reward patterns and emotional associations with smoking</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Reframing your relationship with smoking:</strong> Shifting from "giving up" to "gaining freedom" - becoming a non-smoker rather than a smoker trying not to smoke</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Hypnotherapy session:</strong> Deep relaxation while we work with your subconscious to rewire smoking habits and strengthen your identity as a non-smoker</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Practical strategies:</strong> Tools for handling stress, social situations, and any remaining triggers without smoking</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Lifetime support:</strong> If you ever feel tempted to smoke again, you can return for a free top-up session</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Heart className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Money-Back Guarantee Removes the Risk
                    </h3>
                    <p className="text-gray-700 mb-4">
                      I offer a straightforward money-back guarantee because I'm confident in the approach. If you attend your intensive session, follow the program recommendations, and return within 3 months still smoking, you'll receive a full refund. No questions asked, no hidden conditions.
                    </p>
                    <p className="text-gray-700">
                      This guarantee is possible because the majority of genuinely motivated clients succeed. It removes the financial risk for you. The only thing you're risking is staying trapped in smoking for the rest of your life, spending thousands of pounds per year on a habit that's slowly killing you. When you look at it that way, the real risk is in not trying.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              The Financial Freedom of Quitting Smoking
            </h2>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 mb-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                ROI That Makes Sense for Carrickfergus Families
              </h3>
              <p className="text-gray-700 mb-6">
                For hospitality workers and families in Carrickfergus, the financial impact of smoking is significant. Let's be honest about the money:
              </p>
              <div className="bg-white rounded-xl p-6 mb-6">
                <p className="text-sm text-gray-600 mb-2">Annual Smoking Cost (20/day)</p>
                <p className="text-2xl font-bold text-gray-900 mb-1">Thousands of pounds a year</p>
                <p className="text-sm text-gray-600">Hundreds of pounds every month going up in smoke</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <p className="text-lg font-semibold text-gray-900 mb-3">Your Investment Pays Back In:</p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>3 weeks</strong> - The cost of the session is recouped</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>2 months</strong> - You've saved enough to matter for family expenses</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>1 year</strong> - A year of cigarette money could pay for a family holiday, car repairs, debt repayment, emergency fund</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>10 years</strong> - A decade of savings could be a house deposit, children's education, secure retirement</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                For families in Carrickfergus, that money each month could transform your financial situation. It's not just about health - though that matters enormously. It's about financial freedom. It's about setting a better example for your children. It's about not smelling of smoke when serving customers. It's about taking back control of your life and your money.
              </p>
              <p>
                When you frame it this way, the investment in hypnotherapy isn't a cost - it's one of the best financial decisions you can make. And with the money-back guarantee, there's literally no financial risk. The only risk is staying trapped in smoking and watching thousands of pounds burn away every year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hospitality Worker Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Quitting Smoking for Hospitality Workers
            </h2>
            <div className="bg-white rounded-xl p-8">
              <div className="flex items-start space-x-4">
                <Users className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Fresh Breath and Professional Confidence
                  </h3>
                  <p className="text-gray-700 mb-4">
                    If you work in hospitality - serving customers at Carrickfergus Castle tours, hotel front desk, restaurant service, or retail - you know the embarrassment of cigarette breath when interacting face-to-face with customers. You try to mask it with mints or gum, but you know customers can smell cigarettes on your breath and clothes. It's unprofessional, and it affects your confidence when serving people.
                  </p>
                  <p className="text-gray-700 mb-4">
                    Smoking breaks become stress relief from difficult customers, but they also reduce your productivity and create guilt about taking frequent breaks. The social ritual of smoking with coworkers feels essential, but it's costing you money and health. Hypnotherapy addresses these specific hospitality industry smoking patterns.
                  </p>
                  <p className="text-gray-700">
                    Quitting smoking means showing up to work fresh and confident, no longer worrying about whether customers can smell cigarettes, handling customer service pressure without needing to escape for smoking breaks, saving money instead of spending it on cigarettes during shifts, and setting a better example as a professional. For hospitality workers, quitting smoking isn't just about health - it's about professional pride and career confidence.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Carrickfergus Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I work as a tour guide at the castle and the cigarette smell was really embarrassing when speaking to groups. I'd try to mask it with mints but I knew people could tell. After Sharon's intensive session, I haven't smoked in 7 months. The confidence boost from not smelling of cigarettes is incredible. I actually enjoy the tours again instead of feeling self-conscious."
                </p>
                <p className="font-semibold text-gray-900">J.K., Carrickfergus - Hospitality worker, 15-year smoking habit</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I'd switched from smoking to vaping, thinking it was healthier, but I was vaping constantly throughout my retail shifts. Sharon explained how the psychological habit was still there. One session and I'm done with nicotine completely. The cost seemed expensive until I worked out how much I was spending every month on vape products. Best investment I ever made."
                </p>
                <p className="font-semibold text-gray-900">P.M., Greenisland - Vaping cessation</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working in hospitality with customer stress, smoking breaks felt like the only way to cope. Sharon helped me understand I was using cigarettes as a crutch for managing difficult customers. Five months smoke-free and I'm handling work stress better without needing to escape for cigarettes. I've saved a serious amount and my family actually sees the money difference. The drive from Carrickfergus was nothing - barely 15 minutes."
                </p>
                <p className="font-semibold text-gray-900">A.D., East Antrim - Customer service stress smoking</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Carrickfergus Quit Smoking
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How much does quit smoking hypnotherapy cost in Carrickfergus?
                </h3>
                <p className="text-gray-700">
                  The intensive 2-hour quit smoking session includes lifetime support and a money-back guarantee. For clients who prefer a gradual approach, a 3-session package is also available. To put this in perspective: a 20-a-day smoker spends thousands of pounds a year on cigarettes. You recoup the cost of the session in just 3 weeks of not smoking. For hospitality workers in Carrickfergus, this represents less than one week's wages for potentially life-changing results.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What are realistic success rates for quit smoking hypnotherapy?
                </h3>
                <p className="text-gray-700">
                  I'm honest about success rates because trust matters. Research shows that at 12 months, approximately 20-30% of motivated clients remain smoke-free with hypnotherapy support. While some studies report higher short-term rates, long-term success is more modest. The evidence for hypnotherapy is mixed - some studies show good results, others show less dramatic effects. What matters most is your genuine motivation to quit. Hypnotherapy works best when you're truly ready to become a non-smoker, not when you're being pressured by others.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does the money-back guarantee really work?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. If you attend your intensive session, follow the program recommendations, and return within 3 months still smoking, you'll receive a full refund. This guarantee is possible because the majority of genuinely motivated clients succeed. It removes the financial risk and demonstrates my confidence in the approach. The guarantee terms are clear and straightforward - no hidden conditions.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help hospitality workers quit smoking?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Hospitality workers face unique smoking challenges - smoking breaks as stress relief from difficult customers, the social ritual of smoking with coworkers, concern about cigarette breath when serving customers, and the habit of smoking after shifts. Hypnotherapy addresses these specific triggers by rewiring the subconscious patterns that link work stress with cigarettes, developing alternative behaviors for break times, and building confidence in managing customer service pressure without needing to smoke. The approach is tailored to hospitality industry smoking patterns.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for vaping cessation?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. The approach works equally well for vaping cessation as it does for cigarettes. While vaping is marketed as 'healthier' than smoking, it still maintains the nicotine addiction and the psychological habit. Many people who switched from smoking to vaping find themselves just as dependent, vaping constantly throughout the day. The hypnotherapy program addresses both the physical nicotine addiction and the psychological patterns that keep you reaching for your vape.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Belfast from Carrickfergus for quit smoking sessions?
                </h3>
                <p className="text-gray-700">
                  Belfast is just 12 miles south of Carrickfergus via the A2 coastal route and M2 motorway. By car, the journey takes 15-20 minutes in typical traffic conditions. Many Carrickfergus residents already travel to Belfast regularly for work or shopping, making this a familiar route. The short journey makes professional smoking cessation support highly accessible. Online sessions are also available if you prefer the convenience of attending from home.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Why focus on fresh breath for hospitality workers?
                </h3>
                <p className="text-gray-700">
                  For hospitality workers serving customers face-to-face, cigarette breath can be genuinely embarrassing and unprofessional. Tour guides at Carrickfergus Castle, hotel staff, restaurant servers, and retail workers all interact closely with customers. Smoking before or during shifts means serving customers while smelling of cigarettes. Quitting smoking means showing up to work fresh, confident, and professional - no longer worrying about whether customers can smell cigarette smoke on your breath or clothes. This professional confidence is a significant quality-of-life improvement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default CarrickfergusQuitSmoking;
